// src/components/backoffice/users/UsersPage.tsx

import React, { useState, useEffect, useCallback } from "react";
import {
  getUsers,
  getUserDetails,
  saveUser,
  toggleUserBlock,
  toggleFreeAccess,
  grantManualAccess,
  updateManualAccess,
  revokeManualAccess,
  exportUsers,
  UserDetails,
  GetUsersParams,
  GetUsersResponse,
  SaveUserData,
  User,
} from "../../../services/backoffice/userService";

interface UsersPageProps {}
const isUserBlocked = (user: User | UserDetails): boolean => {
  return Boolean(user.blocked === 1 || user.is_blocked === true);
};

const hasUserFreeAccess = (user: User | UserDetails): boolean => {
  return Boolean(user.free_access);
};

// המנוי הפעיל של המשתמש (status פעיל + end_date עתידי), אחרת null
const getActiveSub = (user: User | UserDetails) => {
  const s = (user as User).current_subscription;
  if (!s) return null;
  const isActive =
    (s.status === "active" || s.status === "trial") &&
    !!s.end_date &&
    new Date(s.end_date as string) > new Date();
  return isActive ? s : null;
};

// const isUserVerified = (user: User | UserDetails): boolean => {
//   return !!(user as UserDetails).verified;
// };
const UsersPage: React.FC<UsersPageProps> = () => {
  // State for users data
  const [usersData, setUsersData] = useState<GetUsersResponse>({
    users: [],
    total: 0,
    page: 1,
    limit: 10,
    totalPages: 0,
  });

  // State for loading and errors
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState<{
    [key: number]: boolean;
  }>({});

  // State for filters
  const [filters, setFilters] = useState<GetUsersParams>({
    page: 1,
    limit: 10,
    search: "",
    user_type: "",
    is_blocked: null,
    free_access: null,
    verified: null,
    has_subscription: "",
  });
  const [exportLoading, setExportLoading] = useState<boolean>(false);

  // State for add/edit user
  const [editingUser, setEditingUser] = useState<UserDetails | null>(null);
  const [showAddForm, setShowAddForm] = useState<boolean>(false);
  const [formData, setFormData] = useState<SaveUserData>({
    id: undefined,
    email: "",
    name: "", // ✅ הוספת שדה שם
    phone_number: "",
    password: "",
    user_type: "personal",
    company_name: "",
    blocked: false,
    verified: false,
    free_access: false,
  });
  const [formLoading, setFormLoading] = useState<boolean>(false);

  // גישה מוגבלת (הרשאת אדמין)
  const [manualAccessUser, setManualAccessUser] = useState<User | null>(null);
  const [manualAccessDate, setManualAccessDate] = useState<string>("");
  const [manualAccessLoading, setManualAccessLoading] = useState<boolean>(false);

  // Load users function - מוגדר מוקדם כדי שיהיה זמין לפונקציות אחרות
  const loadUsers = useCallback(async () => {
    try {
      console.log("טוען משתמשים עם פילטרים:", filters); // ✅ debug
      setLoading(true);
      setError(null);
      const data = await getUsers(filters);
      console.log("התקבלו משתמשים:", data); // ✅ debug
      setUsersData(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : "שגיאה בטעינת המשתמשים");
      console.error("Error loading users:", err);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  // Load users on component mount and filter changes
  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  // useEffect(() => {
  //   if (editingUser && showAddForm) {
  //     console.log('editingUser השתנה:', editingUser);
  //     console.log('formData נוכחי:', formData);
  //   }
  // }, [editingUser, showAddForm]);

  // Handle filter changes
  const handleFilterChange = (key: keyof GetUsersParams, value: any) => {
    console.log(`פילטר ${key} השתנה ל:`, value);

    setFilters((prev) => {
      const newFilters = {
        ...prev,
        [key]: value,
        page: key !== "page" ? 1 : value,
      };

      console.log("פילטרים חדשים:", newFilters);
      return newFilters;
    });
  };

  // Handle search
  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    // The search will trigger automatically via useEffect
  };

  // Handle pagination
  const handlePageChange = (newPage: number) => {
    handleFilterChange("page", newPage);
  };

  // Handle user block toggle
  const handleToggleBlock = async (userId: number) => {
    if (!window.confirm("האם אתה בטוח שברצונך לשנות את סטטוס החסימה?")) {
      return;
    }

    try {
      setActionLoading((prev) => ({ ...prev, [userId]: true }));

      const currentUser = usersData.users.find((user) => user.id === userId);
      if (!currentUser) {
        throw new Error("משתמש לא נמצא");
      }

      // ✅ קריאה נכונה של הסטטוס הנוכחי - בדיקה כפולה
      const isCurrentlyBlocked = isUserBlocked(currentUser);

      await toggleUserBlock(userId, !isCurrentlyBlocked);
      await loadUsers();
    } catch (err) {
      console.error("שגיאה בחסימת המשתמש:", err);
      setError(err instanceof Error ? err.message : "שגיאה בחסימת המשתמש");
    } finally {
      setActionLoading((prev) => ({ ...prev, [userId]: false }));
    }
  };
  // Handle free access toggle - ✅ ללא פרמטר נוסף
  const handleToggleFreeAccess = async (userId: number) => {
    if (!window.confirm("האם אתה בטוח שברצונך לשנות את סטטוס הגישה החופשית?")) {
      return;
    }

    try {
      setActionLoading((prev) => ({ ...prev, [userId]: true }));

      const currentUser = usersData.users.find((user) => user.id === userId);
      if (!currentUser) {
        throw new Error("משתמש לא נמצא");
      }

      // ✅ שימוש בפונקציה העוזרת
      const hasCurrentlyFreeAccess = hasUserFreeAccess(currentUser);

      await toggleFreeAccess(userId, !hasCurrentlyFreeAccess);
      await loadUsers();
    } catch (err) {
      console.error("שגיאה בשינוי גישה חופשית:", err);
      setError(err instanceof Error ? err.message : "שגיאה בשינוי גישה חופשית");
    } finally {
      setActionLoading((prev) => ({ ...prev, [userId]: false }));
    }
  };

  // ===== גישה מוגבלת (הרשאת אדמין) =====
  const openManualAccess = (user: User) => {
    const active = getActiveSub(user);
    const isManual = !!active && active.admin_granted === 1;
    // עריכת גישה קיימת → מציגים את התאריך הנוכחי; אחרת ריק
    setManualAccessDate(
      isManual && active?.end_date ? String(active.end_date).slice(0, 10) : ""
    );
    setManualAccessUser(user);
  };

  const closeManualAccess = () => {
    setManualAccessUser(null);
    setManualAccessDate("");
  };

  const submitManualAccess = async () => {
    if (!manualAccessUser) return;
    if (!manualAccessDate) {
      setError("יש לבחור תאריך סיום");
      return;
    }
    const active = getActiveSub(manualAccessUser);
    const isManual = !!active && active.admin_granted === 1;
    const endDate = `${manualAccessDate} 23:59:59`; // גישה עד סוף היום שנבחר

    try {
      setManualAccessLoading(true);
      if (isManual) {
        await updateManualAccess(manualAccessUser.id, endDate);
      } else {
        await grantManualAccess(manualAccessUser.id, endDate);
      }
      closeManualAccess();
      await loadUsers();
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "שגיאה בהענקת גישה מוגבלת"
      );
    } finally {
      setManualAccessLoading(false);
    }
  };

  const handleRevokeManualAccess = async () => {
    if (!manualAccessUser) return;
    if (!window.confirm("לבטל את הגישה המוגבלת של המשתמש?")) return;
    try {
      setManualAccessLoading(true);
      await revokeManualAccess(manualAccessUser.id);
      closeManualAccess();
      await loadUsers();
    } catch (err) {
      setError(err instanceof Error ? err.message : "שגיאה בביטול הגישה");
    } finally {
      setManualAccessLoading(false);
    }
  };

  // Handle edit user

  const handleEditUser = async (userId: number) => {
    try {
      setLoading(true);
      console.log("🔥 מתחיל עריכת משתמש:", userId);

      const userDetails = await getUserDetails(userId);
      console.log("🔥 נתוני משתמש שהתקבלו:", userDetails);

      // Debug each field individually
      console.log("🔥 userDetails.id:", userDetails.id);
      console.log("🔥 userDetails.name:", userDetails.name);
      console.log("🔥 userDetails.email:", userDetails.email);
      console.log("🔥 userDetails.phone_number:", userDetails.phone_number);
      console.log("🔥 userDetails.user_type:", userDetails.user_type);
      console.log("🔥 userDetails.company_name:", userDetails.company_name);
      console.log("🔥 userDetails.blocked:", userDetails.blocked);
      console.log("🔥 userDetails.verified:", userDetails.verified);
      console.log("🔥 userDetails.free_access:", userDetails.free_access);

      // First set the editing user
      setEditingUser(userDetails);
      console.log("🔥 editingUser עודכן");

      // Fix: Use the exact field names from the server response
      const newFormData = {
        id: userDetails.id,
        name: userDetails.name || "",
        email: userDetails.email || "",
        phone_number: userDetails.phone_number || "",
        password: "", // Always empty for edit
        user_type: userDetails.user_type || "personal",
        company_name: userDetails.company_name || "",
        blocked: !!userDetails.blocked,
        verified: !!userDetails.verified,
        free_access: !!userDetails.free_access,
      };

      console.log("🔥 FormData חדש שיוגדר:", newFormData);

      // Set form data
      setFormData(newFormData);
      console.log("🔥 setFormData נקרא");

      // Show form after a delay to ensure state is updated
      setTimeout(() => {
        setShowAddForm(true);
        console.log("🔥 showAddForm הוגדר ל-true");
      }, 100);
    } catch (err) {
      console.error("שגיאה בטעינת פרטי המשתמש:", err);
      setError(err instanceof Error ? err.message : "שגיאה בטעינת פרטי המשתמש");
    } finally {
      setLoading(false);
    }
  };
  // Handle add new user
  const handleAddUser = () => {
    setEditingUser(null);
    setFormData({
      email: "",
      name: "",
      phone_number: "",
      password: "",
      user_type: "personal",
      company_name: "",
      blocked: false,
      verified: false,
      free_access: false,
    });
    setShowAddForm(true);
  };

  // Handle form submit
  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setFormLoading(true);
      await saveUser(formData);
      setShowAddForm(false);
      setEditingUser(null);
      loadUsers(); // Refresh the list
    } catch (err) {
      setError(err instanceof Error ? err.message : "שגיאה בשמירת המשתמש");
    } finally {
      setFormLoading(false);
    }
  };

  // Handle form cancel
  const handleFormCancel = () => {
    setShowAddForm(false);
    setEditingUser(null);
    setFormData({
      email: "",
      name: "",
      phone_number: "",
      password: "",
      user_type: "personal",
      company_name: "",
      blocked: false,
      verified: false,
      free_access: false,
    });
  };
  // Handle export to Excel
  const handleExportExcel = async () => {
    try {
      setExportLoading(true);
      const users = await exportUsers(filters);

      // Build CSV content with BOM for Hebrew support
      const BOM = "\uFEFF";
      const headers = [
        "מזהה",
        "שם",
        "אימייל",
        "טלפון",
        "סוג משתמש",
        "חברה",
        "מאומת",
        "חסום",
        "גישה חופשית",
        "מנוי",
        "תאריך הצטרפות",
      ];
      const rows = users.map((user: any) => [
        user.id,
        user.name,
        user.email,
        user.phone_number,
        user.user_type,
        user.company_name,
        user.verified,
        user.blocked,
        user.free_access,
        user.subscription,
        user.join_date
          ? new Date(user.join_date).toLocaleDateString("he-IL")
          : "",
      ]);

      const csvContent =
        BOM +
        [headers, ...rows]
          .map((row) =>
            row
              .map((cell: any) => `"${String(cell || "").replace(/"/g, '""')}"`)
              .join(","),
          )
          .join("\n");

      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = `users_export_${new Date().toISOString().slice(0, 10)}.csv`;
      link.click();
      URL.revokeObjectURL(link.href);
    } catch (err) {
      setError(err instanceof Error ? err.message : "שגיאה ביצוא המשתמשים");
    } finally {
      setExportLoading(false);
    }
  };
  // Format date for display
  const formatDate = (dateString: string | undefined) => {
    if (!dateString) return "לא זמין";

    try {
      const date = new Date(dateString);

      if (isNaN(date.getTime())) {
        console.warn("תאריך לא תקין:", dateString);
        return "תאריך לא תקין";
      }

      return date.toLocaleDateString("he-IL", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch (error) {
      console.error("שגיאה בפורמט תאריך:", error, dateString);
      return "שגיאה בתאריך";
    }
  };
  // Get user type display text
  const getUserTypeText = (type: string) => {
    return type === "personal" ? "אישי" : "עסקי";
  };

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-800">ניהול משתמשים 👥</h1>
        <div className="flex gap-3">
          <button
            onClick={handleExportExcel}
            disabled={exportLoading}
            className="bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded-lg transition-colors disabled:opacity-50"
          >
            {exportLoading ? "מייצא..." : "📥 יצוא אקסל"}
          </button>
          <button
            onClick={handleAddUser}
            className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-lg transition-colors"
          >
            הוספת משתמש חדש
          </button>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}

      {/* Add/Edit User Form */}
      {showAddForm && (
        <div className="bg-white p-6 rounded-lg shadow-md mb-6">
          <h2 className="text-xl font-semibold mb-4">
            {editingUser
              ? `עריכת משתמש: ${editingUser.email}`
              : "הוספת משתמש חדש"}
          </h2>
          <form onSubmit={handleFormSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                כתובת אימייל
              </label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, email: e.target.value }))
                }
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                שם מלא
              </label>
              <input
                type="text"
                value={formData.name || ""}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, name: e.target.value }))
                }
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="שם המשתמש המלא"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                מספר טלפון
              </label>
              <input
                type="tel"
                value={formData.phone_number}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    phone_number: e.target.value,
                  }))
                }
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            {!editingUser && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  סיסמה
                </label>
                <input
                  type="password"
                  value={formData.password}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      password: e.target.value,
                    }))
                  }
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  required
                />
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                סוג משתמש
              </label>
              <select
                value={formData.user_type}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    user_type: e.target.value as "personal" | "commercial",
                  }))
                }
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="personal">אישי</option>
                <option value="commercial">עסקי</option>
              </select>
            </div>

            {formData.user_type === "commercial" && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  שם חברה *
                </label>
                <input
                  type="text"
                  value={formData.company_name}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      company_name: e.target.value,
                    }))
                  }
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  required={formData.user_type === "commercial"}
                  placeholder="שם החברה חובה למשתמשים עסקיים"
                />
              </div>
            )}

            <div className="space-y-2">
              <label className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked={formData.free_access}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      free_access: e.target.checked,
                    }))
                  }
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="text-sm text-gray-700">
                  גישה חופשית (ללא תשלום)
                </span>
              </label>

              <label className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked={formData.verified || false}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      verified: e.target.checked,
                    }))
                  }
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="text-sm text-gray-700">משתמש מאומת</span>
              </label>

              <label className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked={formData.blocked || false}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      blocked: e.target.checked,
                    }))
                  }
                  className="rounded border-gray-300 text-red-600 focus:ring-red-500"
                />
                <span className="text-sm text-gray-700">חסום</span>
              </label>
            </div>

            <div className="flex space-x-4">
              <button
                type="submit"
                disabled={formLoading}
                className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-md disabled:opacity-50 transition-colors"
              >
                {formLoading ? "שומר..." : "שמור"}
              </button>
              <button
                type="button"
                onClick={handleFormCancel}
                className="bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded-md transition-colors"
              >
                ביטול
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white p-4 rounded-lg shadow-md mb-6">
        <form
          onSubmit={handleSearch}
          className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-7 gap-4"
        >
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              חיפוש
            </label>
            <input
              type="text"
              value={filters.search}
              onChange={(e) => handleFilterChange("search", e.target.value)}
              placeholder="חפש לפי אימייל..."
              className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              סוג משתמש
            </label>
            <select
              value={filters.user_type}
              onChange={(e) => handleFilterChange("user_type", e.target.value)}
              className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">הכל</option>
              <option value="personal">אישי</option>
              <option value="commercial">עסקי</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              סטטוס חסימה
            </label>
            <select
              value={
                filters.is_blocked === null || filters.is_blocked === undefined
                  ? ""
                  : filters.is_blocked === true
                    ? "1"
                    : "0" // ✅ המרה נכונה
              }
              onChange={(e) =>
                handleFilterChange(
                  "is_blocked",
                  e.target.value === "" ? null : e.target.value === "1", // ✅ שמירה כ-boolean אבל שליחה כ-1/0
                )
              }
              className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">הכל</option>
              <option value="0">פעיל</option>
              <option value="1">חסום</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              גישה חופשית
            </label>
            <select
              value={
                filters.free_access === null ||
                filters.free_access === undefined
                  ? ""
                  : filters.free_access === true
                    ? "1"
                    : "0" // ✅ המרה נכונה
              }
              onChange={(e) =>
                handleFilterChange(
                  "free_access",
                  e.target.value === "" ? null : e.target.value === "1", // ✅ שמירה כ-boolean אבל שליחה כ-1/0
                )
              }
              className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">הכל</option>
              <option value="1">חופשי</option>
              <option value="0">רגיל</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              פריטים בעמוד
            </label>
            <select
              value={filters.limit}
              onChange={(e) =>
                handleFilterChange("limit", parseInt(e.target.value))
              }
              className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="10">10</option>
              <option value="25">25</option>
              <option value="50">50</option>
              <option value="100">100</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              סטטוס אימות
            </label>
            <select
              value={
                filters.verified === null || filters.verified === undefined
                  ? ""
                  : filters.verified === true
                    ? "1"
                    : "0"
              }
              onChange={(e) =>
                handleFilterChange(
                  "verified",
                  e.target.value === "" ? null : e.target.value === "1",
                )
              }
              className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">הכל</option>
              <option value="1">מאומת</option>
              <option value="0">לא מאומת</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              מנוי פעיל
            </label>
            <select
              value={filters.has_subscription || ""}
              onChange={(e) =>
                handleFilterChange("has_subscription", e.target.value)
              }
              className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">הכל</option>
              <option value="1">עם מנוי</option>
              <option value="0">ללא מנוי</option>
            </select>
          </div>
        </form>
      </div>

      {/* Users Table */}
      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        {loading ? (
          <div className="p-8 text-center">
            <div className="text-gray-500">טוען משתמשים...</div>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-sm text-right text-gray-500">
                <thead className="text-xs text-gray-700 uppercase bg-gray-50">
                  <tr>
                    <th className="px-6 py-3">ID</th>
                    <th className="px-6 py-3">אימייל</th>
                    <th className="px-6 py-3">טלפון</th>
                    <th className="px-6 py-3">סוג מנוי</th>
                    <th className="px-6 py-3">סוג משתמש</th>
                    <th className="px-6 py-3">סטטוס</th>
                    <th className="px-6 py-3">גישה חופשית</th>
                    <th className="px-6 py-3">תאריך הצטרפות</th>
                    <th className="px-6 py-3">פעולות</th>
                  </tr>
                </thead>
                <tbody>
                  {usersData.users.map((user) => (
                    <tr
                      key={user.id}
                      className="bg-white border-b hover:bg-gray-50"
                    >
                      <td className="px-6 py-4 font-medium text-gray-900">
                        {user.id}
                      </td>
                      <td className="px-6 py-4">{user.email}</td>
                      <td className="px-6 py-4">{user.phone_number || "-"}</td>
                      <td className="px-6 py-4">
                        {user.current_subscription &&
                        user.current_subscription.status !== "cancelled" &&
                        user.current_subscription.status !== "expired" &&
                        user.current_subscription.end_date &&
                        new Date(user.current_subscription.end_date) >
                          new Date() ? (
                          <span
                            className={`px-2 py-1 text-xs rounded-full ${
                              user.current_subscription.plan_name ===
                              "yearly_plan"
                                ? "bg-purple-100 text-purple-800"
                                : user.current_subscription.plan_name ===
                                    "quarterly_plan"
                                  ? "bg-blue-100 text-blue-800"
                                  : "bg-green-100 text-green-800"
                            }`}
                          >
                            {user.current_subscription.plan_name ===
                            "yearly_plan"
                              ? "שנתי"
                              : user.current_subscription.plan_name ===
                                  "quarterly_plan"
                                ? "רבעוני"
                                : user.current_subscription.plan_name ===
                                    "monthly_plan"
                                  ? "חודשי"
                                  : user.current_subscription.plan_name ===
                                      "daily_plan"
                                    ? "יומי"
                                    : user.current_subscription.plan_name}
                          </span>
                        ) : (
                          <span className="px-2 py-1 text-xs rounded-full bg-gray-100 text-gray-800">
                            ללא מנוי
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        {getUserTypeText(user.user_type)}
                      </td>
                      <td className="px-6 py-4">
                        <span
                          className={`px-2 py-1 text-xs rounded-full ${
                            isUserBlocked(user)
                              ? "bg-red-100 text-red-800"
                              : "bg-green-100 text-green-800"
                          }`}
                        >
                          {isUserBlocked(user) ? "חסום" : "פעיל"}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <span
                          className={`px-2 py-1 text-xs rounded-full ${
                            hasUserFreeAccess(user)
                              ? "bg-blue-100 text-blue-800"
                              : "bg-gray-100 text-gray-800"
                          }`}
                        >
                          {hasUserFreeAccess(user) ? "חופשי" : "רגיל"}
                        </span>
                      </td>

                      <td className="px-6 py-4">
                        {formatDate(user.ts || user.created_at)}
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex space-x-2">
                          <button
                            onClick={() => handleEditUser(user.id)}
                            className="text-blue-600 hover:text-blue-800 text-sm"
                          >
                            ערוך
                          </button>

                          {/* ✅ כפתור חסימה עם צבעים דינמיים */}
                          <button
                            onClick={() => handleToggleBlock(user.id)}
                            disabled={actionLoading[user.id]}
                            className={`text-sm font-medium px-2 py-1 rounded transition-colors ${
                              isUserBlocked(user)
                                ? "text-green-600 hover:text-green-800 bg-green-50 hover:bg-green-100"
                                : "text-red-600 hover:text-red-800 bg-red-50 hover:bg-red-100"
                            } disabled:opacity-50 disabled:cursor-not-allowed`}
                          >
                            {actionLoading[user.id]
                              ? "מעדכן..."
                              : isUserBlocked(user)
                                ? "🔓 בטל חסימה"
                                : "🔒 חסום"}
                          </button>

                          <button
                            onClick={() => handleToggleFreeAccess(user.id)}
                            disabled={actionLoading[user.id]}
                            className={`text-sm font-medium px-2 py-1 rounded transition-colors ${
                              hasUserFreeAccess(user)
                                ? "text-orange-600 hover:text-orange-800 bg-orange-50 hover:bg-orange-100"
                                : "text-purple-600 hover:text-purple-800 bg-purple-50 hover:bg-purple-100"
                            } disabled:opacity-50 disabled:cursor-not-allowed`}
                          >
                            {actionLoading[user.id]
                              ? "מעדכן..."
                              : hasUserFreeAccess(user)
                                ? "💎 בטל חופשי"
                                : "🎁 גישה חופשית"}
                          </button>

                          <button
                            onClick={() => openManualAccess(user)}
                            className="text-sm font-medium px-2 py-1 rounded transition-colors text-indigo-600 hover:text-indigo-800 bg-indigo-50 hover:bg-indigo-100"
                          >
                            {getActiveSub(user)?.admin_granted === 1
                              ? "⏳ גישה עד " +
                                formatDate(getActiveSub(user)?.end_date)
                              : "⏳ גישה מוגבלת"}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {usersData.totalPages > 1 && (
              <div className="px-6 py-4 bg-gray-50 border-t">
                <div className="flex items-center justify-between">
                  <div className="text-sm text-gray-700">
                    מציג {(usersData.page - 1) * usersData.limit + 1} עד{" "}
                    {Math.min(
                      usersData.page * usersData.limit,
                      usersData.total,
                    )}{" "}
                    מתוך {usersData.total} משתמשים
                  </div>
                  <div className="flex space-x-2">
                    <button
                      onClick={() => handlePageChange(usersData.page - 1)}
                      disabled={usersData.page === 1}
                      className="px-3 py-1 text-sm bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      קודם
                    </button>

                    {(() => {
                      const currentPage = usersData.page;
                      const totalPages = usersData.totalPages;
                      const pages: (number | string)[] = [];

                      if (totalPages <= 7) {
                        for (let i = 1; i <= totalPages; i++) pages.push(i);
                      } else {
                        pages.push(1);
                        if (currentPage > 3) pages.push("...");
                        for (
                          let i = Math.max(2, currentPage - 1);
                          i <= Math.min(totalPages - 1, currentPage + 1);
                          i++
                        ) {
                          pages.push(i);
                        }
                        if (currentPage < totalPages - 2) pages.push("...");
                        pages.push(totalPages);
                      }

                      return pages.map((pageNum, index) =>
                        pageNum === "..." ? (
                          <span
                            key={`dots-${index}`}
                            className="px-2 py-1 text-sm text-gray-400"
                          >
                            ...
                          </span>
                        ) : (
                          <button
                            key={pageNum}
                            onClick={() => handlePageChange(pageNum as number)}
                            className={`px-3 py-1 text-sm border rounded-md ${
                              pageNum === currentPage
                                ? "bg-blue-500 text-white border-blue-500"
                                : "bg-white border-gray-300 hover:bg-gray-50"
                            }`}
                          >
                            {pageNum}
                          </button>
                        ),
                      );
                    })()}

                    <button
                      onClick={() => handlePageChange(usersData.page + 1)}
                      disabled={usersData.page === usersData.totalPages}
                      className="px-3 py-1 text-sm bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      הבא
                    </button>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* מודל: גישה מוגבלת (הרשאת אדמין) */}
      {manualAccessUser &&
        (() => {
          const active = getActiveSub(manualAccessUser);
          const isManual = !!active && active.admin_granted === 1;
          const isPaidActive = !!active && !isManual;
          return (
            <div
              className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50"
              onClick={closeManualAccess}
            >
              <div
                className="bg-white rounded-lg shadow-xl w-full max-w-md p-6 text-right"
                dir="rtl"
                onClick={(e) => e.stopPropagation()}
              >
                <h3 className="text-lg font-bold text-gray-900 mb-1">
                  גישה מוגבלת — {manualAccessUser.name || manualAccessUser.email}
                </h3>
                <p className="text-sm text-gray-500 mb-4">
                  גישה זמנית ללא תשלום. תישלח תזכורת אוטומטית 24 שעות לפני הסיום,
                  והגישה תיחסם אוטומטית בתאריך שנבחר.
                </p>

                {isPaidActive ? (
                  <div className="bg-yellow-50 border border-yellow-300 rounded p-3 text-sm text-gray-700">
                    למשתמש יש כבר מנוי פעיל עד{" "}
                    <strong>{formatDate(active?.end_date)}</strong>. לא ניתן
                    להעניק גישה ידנית עד לסיומו.
                  </div>
                ) : (
                  <>
                    {isManual && (
                      <div className="bg-indigo-50 border border-indigo-200 rounded p-3 text-sm text-gray-700 mb-3">
                        גישה מוגבלת פעילה עד{" "}
                        <strong>{formatDate(active?.end_date)}</strong>.
                      </div>
                    )}
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      תאריך סיום גישה
                    </label>
                    <input
                      type="date"
                      value={manualAccessDate}
                      onChange={(e) => setManualAccessDate(e.target.value)}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 mb-4"
                    />
                  </>
                )}

                <div className="flex justify-between items-center gap-2">
                  <div>
                    {isManual && (
                      <button
                        onClick={handleRevokeManualAccess}
                        disabled={manualAccessLoading}
                        className="text-sm font-medium px-3 py-2 rounded text-red-600 hover:text-red-800 bg-red-50 hover:bg-red-100 disabled:opacity-50"
                      >
                        בטל גישה
                      </button>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={closeManualAccess}
                      disabled={manualAccessLoading}
                      className="text-sm px-3 py-2 rounded border border-gray-300 hover:bg-gray-50 disabled:opacity-50"
                    >
                      סגור
                    </button>
                    {!isPaidActive && (
                      <button
                        onClick={submitManualAccess}
                        disabled={manualAccessLoading}
                        className="text-sm font-medium px-4 py-2 rounded bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50"
                      >
                        {manualAccessLoading
                          ? "שומר..."
                          : isManual
                            ? "עדכן תאריך"
                            : "הענק גישה"}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          );
        })()}
    </div>
  );
};

export default UsersPage;
